import { Box, Tooltip } from "@mui/material";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Base } from "../../framework/base";
import { AppUtils } from "../../models/common/appUtils";
import { DayBookingUsageType } from "../../models/common/enums";
import { DayBookingItemBeta } from "../../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../../models/dayBookingType/dayBookingTypeItemBeta";
import { showApiError, showApiSuccess } from "../framework/formUtils";
import { LoadingIndicatorInline } from "../framework/loadingIndicatorNew";
import MuiRadioGroup, {
    IMuiRadioGroupOption,
} from "../framework/muiRadioGroup";
import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import { deleteDayBooking, saveDayBooking } from "../../services/dayBookingService";
import { DayBookingSaveData } from "../../models/dayBooking/dayBookingSaveData";
import { deleteCategoryFromWorkShiftTimeSlot } from "../../services/workTimeBetaService";
import { HourBookingCategory } from "../../models/workTime/hourBookingCategory";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";

const createSaveFormData = (
    newTypeId: string,
    employeeId: string,
    date: string,
    vehicleId: string | null,
    costCenterId: string | null
): DayBookingSaveData => ({
    id: "",
    employeeId,
    dayBookingTypeId: newTypeId,
    vehicleId: vehicleId ?? "",
    costCenterId: costCenterId ?? "",
    day: Base.dayjsToJsonDate(date),
    amount: 1,
    dayUsageType: DayBookingUsageType.NotFullDay,
    comment: "",
    rowId: "",
});

interface IDayAllowanceSelectProps {
    reloadList: () => void;
    dayBookingTypes: DayBookingTypeItemBeta[];
    dayBookingItems: DayBookingItemBeta[];
    allWorkTimesLocked: boolean;
    loadingDayBookings: boolean;
    shiftDailyAllowances: IWorkHoursItem[];
    costCenterId: string | undefined;
    vehicleId: string | undefined;
}

export const DayAllowanceSelect = ({
    reloadList,
    dayBookingTypes,
    dayBookingItems,
    allWorkTimesLocked,
    loadingDayBookings,
    shiftDailyAllowances,
    costCenterId,
    vehicleId,
}: IDayAllowanceSelectProps) => {
    const { employeeId, date } = useParams<IWorkTimeDetailsPage>();

    const relevantTypes = useMemo(
        () =>
            dayBookingTypes?.filter(
                (type) =>
                    type.category ===
                        (HourBookingCategory.HalfDailyAllowance as number) ||
                    type.category ===
                        (HourBookingCategory.DailyAllowance as number) ||
                    type.category ===
                        (HourBookingCategory.IncreasedDailyAllowance as number)
            ),
        [dayBookingTypes]
    );

    const existingAllowances: DayBookingItemBeta[] = useMemo(
        () =>
            dayBookingItems?.filter((item) =>
                relevantTypes?.some((type) => type.id === item.dayBookingTypeId)
            ) || [],
        [dayBookingItems, relevantTypes]
    );

    const existingAllowance =
        existingAllowances.length === 1 ? existingAllowances[0] : null;

    const options: IMuiRadioGroupOption[] = relevantTypes?.map((type) => ({
        label: type.name,
        value: type.id,
    }));
    options?.unshift({
        label: AppUtils.translate("dayAllowance.noAllowance"),
        value: "",
    });

    const calcAllowance =
        shiftDailyAllowances.length === 1 ? shiftDailyAllowances[0] : null;
    const calcDayBookingTypeId = relevantTypes.find(
        (t) => t.category.toString() === calcAllowance?.category
    )?.id;

    const handleDayAllowanceChange = async(newTypeId) => {
        let isDeleteOperationPerformed = false;
        let isCreateOperationPerformed = false;

        if (existingAllowance) {
            try {
                await deleteDayBooking(existingAllowance.id);
                isDeleteOperationPerformed = true;
            } catch (error) {
                showApiError(AppUtils.translate("dayAllowance.deleteFail"));
                return;
            }
        }

        if (calcAllowance) {
            try {
                await deleteCategoryFromWorkShiftTimeSlot(
                    calcAllowance.workShiftTimeSlotId,
                    calcAllowance.category,
                    false
                );
                isDeleteOperationPerformed = true;
            } catch (error) {
                showApiError(AppUtils.translate("dayAllowance.deleteFail"));
                return;
            }
        }

        if (newTypeId && newTypeId !== "") {
            const type = relevantTypes.find(t => t.id === newTypeId);

            const saveFormData = createSaveFormData(
                newTypeId as string,
                employeeId,
                date,
                type.useWorkShiftVehicle ? vehicleId : null,
                type.useWorkShiftCostCenter ? costCenterId : null
            );
            try {
                await saveDayBooking(saveFormData);
                isCreateOperationPerformed = true;
            } catch (error) {
                showApiError(AppUtils.translate("dayAllowance.createFail"));
                return;
            }
        }

        if (isDeleteOperationPerformed && isCreateOperationPerformed) {
            showApiSuccess(AppUtils.translate("dayAllowance.updateSuccess"));
        } else if (isDeleteOperationPerformed) {
            showApiSuccess(AppUtils.translate("dayAllowance.deleteSuccess"));
        } else if (isCreateOperationPerformed) {
            showApiSuccess(AppUtils.translate("dayAllowance.createSuccess"));
        }

        handleAfterSaveOrRemove();
    };

    const handleAfterSaveOrRemove = () => {
        reloadList();
    };

    if (
        relevantTypes.length === 0 ||
        existingAllowances.length + shiftDailyAllowances.length > 1
    )
        return null;

    if (loadingDayBookings)
        return (
            <Box display="flex" justifyContent="flex-end" height={42}>
                <LoadingIndicatorInline />
            </Box>
        );

    const disabled = allWorkTimesLocked;
    const dayBookinTypeId =
        existingAllowance?.dayBookingTypeId ?? calcDayBookingTypeId ?? "";

    return (
        <Box display="flex" justifyContent="flex-end">
            {disabled ? (
                <Tooltip
                    title={AppUtils.translate("dayAllowance.editForbidden")}
                >
                    <Box>
                        <MuiRadioGroup
                            options={options}
                            value={dayBookinTypeId}
                            row
                            disabled
                        />
                    </Box>
                </Tooltip>
            ) : (
                <MuiRadioGroup
                    options={options}
                    onChange={(val) => void handleDayAllowanceChange(val)}
                    value={dayBookinTypeId}
                    row
                />
            )}
        </Box>
    );
};
